import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { PaymentForm, StripeErrorsMessages } from './payment-form.interface';
import { PaymentService } from 'services/payment/payment.service';
import { PAYMENT_TYPES, PaymentSettings } from 'services/payment/payment.interface';
import { SquareFormComponent } from 'features/payment-form/square-form/square-form.component';
import { Observable, of, Subject } from 'rxjs';
import { take, startWith } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Selectors as ProfileSelectors } from 'features/profiles-state/+state/profiles-state.state';
import { ExactFormComponent } from 'features/payment-form/exact-form/exact-form.component';
import { EPayFormComponent } from 'features/payment-form/epay-form/epay-form.component';
import { FeesStepInstance } from 'services/professional/step/step.interface';
import { STEP_TYPES } from 'constants/status.constants';
import { getErrorMessage } from 'utils/helpers';
import { MessageQueryService } from '@certemy/ui-components';
import { PAYMENT_FORM_PLACE } from 'features/payment-form/payment-form.constants';
import { AuthorizeNetFormComponent } from 'features/payment-form/authorize-net/authorize-net.component';

@Component({
  selector: 'cm-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentFormComponent implements OnInit, OnDestroy {
  @Input() paymentAmount;
  @Input() isAmountShown = true;
  @Input() stepId = null;
  @Input() endpointUrl: string;
  @Input() additionalPaymentParameters: string;
  @Input() step: FeesStepInstance;
  @Input() place: PAYMENT_FORM_PLACE;
  @Output() initFailed = new EventEmitter();

  PAYMENT_TYPES = PAYMENT_TYPES;

  errors: StripeErrorsMessages[] = [];
  showError = false;
  formData: PaymentForm;
  paymentType: PAYMENT_TYPES;
  publicSettings: PaymentSettings;
  currencyIcon$ = this.store.select(ProfileSelectors.paymentSymbol);

  private dataChange$ = new Subject<PaymentForm>();

  @ViewChild('squareForm') squareForm: SquareFormComponent;
  @ViewChild('exactForm') exactForm: ExactFormComponent;
  @ViewChild('epayForm') epayForm: EPayFormComponent;
  @ViewChild('authorizeNetForm') authorizeNetForm: AuthorizeNetFormComponent;

  constructor(
    private paymentService: PaymentService,
    private changeDetector: ChangeDetectorRef,
    private store: Store<any>,
    private messageService: MessageQueryService,
  ) {}

  ngOnInit() {
    const stepId =
      this.stepId ||
      (this.step && this.step.step_type_id === STEP_TYPES.TEST_FEE && this.step.step_instance_id) ||
      null;
    this.paymentService
      .getPublicPaymentSettings(stepId, this.place)
      .pipe(take(1))
      .subscribe(
        (settings: PaymentSettings) => {
          this.publicSettings = settings;
          this.paymentType = settings.type;
          this.changeDetector.markForCheck();
        },
        (err) => {
          this.messageService.addErrorMessage(getErrorMessage(err));
          this.initFailed.emit();
        },
      );
  }

  ngOnDestroy() {}

  getData(): Observable<PaymentForm | any> {
    if (this.paymentType === PAYMENT_TYPES.SQUARE) {
      this.squareForm.submitForm();
      return this.dataChange$;
    }

    if (this.paymentType === PAYMENT_TYPES.EXACT) {
      return this.dataChange$.pipe(startWith(this.exactForm.getData()));
    }

    if (this.paymentType === PAYMENT_TYPES.EPAY) {
      return this.dataChange$.pipe(startWith(this.epayForm.getData()));
    }

    if (this.isFormValid()) {
      this.errors = [];
      if (this.paymentType === PAYMENT_TYPES.AUTHORIZE_NET) {
        setTimeout(() => this.authorizeNetForm.submitForm());
        return this.authorizeNetForm.getData$();
      }

      return this.dataChange$.pipe(startWith(this.formData));
    }

    this.displayError(this.getFormError(this.formData));
    return of(null);
  }

  onSquareChange(nonce: string) {
    if (nonce) {
      this.dataChange$.next({ data: { cardNonce: nonce }, type: this.PAYMENT_TYPES.SQUARE });
    }
  }

  displayError(errors: StripeErrorsMessages[]) {
    this.showError = true;
    this.errors = errors;
    this.changeDetector.markForCheck();
  }

  onPaymentFormChange(formData: PaymentForm) {
    this.formData = {
      ...formData,
      amount: this.paymentAmount,
    };
  }

  private isFormValid(): boolean {
    return this.formData && Object.values(this.formData.data).every((data) => data.valid);
  }

  private getFormError(form: PaymentForm): StripeErrorsMessages[] {
    if (!form) {
      return [];
    }

    if (form.data.card.errorMessage) {
      return [{ validation: form.data.card.errorMessage }];
    }

    if (!form.data.card.valid) {
      return [{ validation: 'Please provide correct data in required fields.' }];
    }

    if (!form.data.name.valid) {
      return [{ validation: form.data.name.errorMessage }];
    }

    return [];
  }
}
